import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link } from "react-router";
import { fetchJson } from "../../utils";
import PopularVideoStories from "../../blocks/pupular-video-stories";

type Inputs = {
    email: string;
}

type PasswordResetResponse = {
    success: boolean;
    message?: string;
    field?: keyof Inputs;
};

export const PasswordReset = () => {
    const [submitting, setSubmitting] = useState(false);
    const [resetRequested, setResetRequested] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        setError
    } = useForm<Inputs>();

    const onSubmit: SubmitHandler<Inputs> = async (data) => {
        setSubmitting(true);
        try {
            const { json } = await fetchJson<PasswordResetResponse>('/api/password-reset', {
                method: 'POST',
                body: JSON.stringify(data)
            });

            if (json.success) {
                setResetRequested(true);
            } else if (json.field && json.message) {
                setError(json.field, {
                    type: 'manual',
                    message: json.message
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (resetRequested) {
        return (
            <section className="section">
                <div className="inside-wrapper">
                    <h1 className="section__title">Check Your Email</h1>
                    <div className="signbox">
                        <div className="signbox__form form">
                            <p>If we found a matching email address, we've sent you instructions to reset your password.</p>
                            <div className="form__row form__row_smallgap form__note">
                                <Link to="/sign-in">Return to Sign In</Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <>
            <section className="section">
                <div className="inside-wrapper">
                    <h1 className="section__title">Reset Password</h1>
                    <div className="signbox">
                        <div className="signbox__form form">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className={`form__row ${errors.email && 'is-error'}`}>
                                    <label className="form__label">
                                        Email Address
                                    </label>
                                    <div className="form__input-holder">
                                        <input 
                                            type="email" 
                                            className="form__input" 
                                            {...register('email', {
                                                required: 'Email is required',
                                                pattern: {
                                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                    message: 'Invalid email address'
                                                }
                                            })} 
                                        />
                                    </div>
                                    {errors.email && <div className="form__alert">{errors.email.message}</div>}
                                </div>
                                <div className="form__row form__row_smallgap form__btns">
                                    <button 
                                        className="btn btn_cta btn_lg btn_extrapad" 
                                        type="submit" 
                                        disabled={submitting}
                                    >
                                        Reset Password
                                    </button>
                                </div>
                                <div className="form__row form__row_smallgap form__note">
                                    Remember your password? <Link to="/sign-in">Sign In</Link>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
            <PopularVideoStories />
        </>
    );
};

export default PasswordReset;